import {
  Directive,
  HostListener,
  ComponentRef,
  ViewContainerRef,
  Input,
  ComponentFactoryResolver,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { TooltipContentComponent } from 'shared/tooltip/tooltip-content.component';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[tooltip]'
})
export class TooltipDirective implements OnChanges {

  // -------------------------------------------------------------------------
  // Properties
  // -------------------------------------------------------------------------
  private tooltip: ComponentRef<TooltipContentComponent>;
  private visible: boolean;

  // -------------------------------------------------------------------------
  // Constructor
  // -------------------------------------------------------------------------
  constructor(private viewContainerRef: ViewContainerRef,
    private resolver: ComponentFactoryResolver) {
  }

  // -------------------------------------------------------------------------
  // Inputs / Outputs
  // -------------------------------------------------------------------------
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('tooltip')
  content: string | TooltipContentComponent;

  @Input()
  tooltipDisabled: boolean;

  @Input()
  tooltipAnimation = true;

  @Input()
  tooltipPlacement: 'top' | 'bottom' | 'left' | 'right' = 'bottom';

  @Input()
  tooltipShow = false;

  // Watch for changes to the tooltipShow flag
  ngOnChanges(changes: SimpleChanges) {
    const change = changes['tooltipShow'];
    if (!change) {
      return;
    }
    if (change.currentValue !== change.previousValue) {
      if (this.tooltipShow) {
        this.show();
      } else {
        this.hide();
      }
    }
  }

  // -------------------------------------------------------------------------
  // Public Methods
  // -------------------------------------------------------------------------

  @HostListener('focusin')
  @HostListener('mouseenter')
  onHoverIn() {
    this.show();
  }

  @HostListener('focusout')
  @HostListener('mouseleave')
  onHoverOut() {
    this.hide();
  }

  @HostListener('touchstart')
  onTap() {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  show(): void {
    if (this.tooltipDisabled || this.visible) {
      return;
    }

    this.visible = true;
    if (typeof this.content === 'string') {
      const factory = this.resolver.resolveComponentFactory(TooltipContentComponent);
      if (!this.visible) {
        return;
      }

      this.tooltip = this.viewContainerRef.createComponent(factory);
      this.tooltip.instance.hostElement = this.viewContainerRef.element.nativeElement;
      this.tooltip.instance.content = this.content as string;
      this.tooltip.instance.placement = this.tooltipPlacement;
      this.tooltip.instance.animation = this.tooltipAnimation;
    } else {
      const tooltip = this.content as TooltipContentComponent;
      tooltip.hostElement = this.viewContainerRef.element.nativeElement;
      tooltip.placement = this.tooltipPlacement;
      tooltip.animation = this.tooltipAnimation;
      tooltip.show();
    }
  }

  hide(): void {
    // Tooltip is forced to show
    if (this.tooltipShow) {
      return;
    }
    if (!this.visible) {
      return;
    }

    this.visible = false;
    if (this.tooltip) {
      this.tooltip.destroy();
    }

    if (this.content instanceof TooltipContentComponent) {
      (this.content as TooltipContentComponent).hide();
    }
  }

}
